import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Envelope, Plus, Folder, FolderPlus, FileText, Trash, MagnifyingGlass,
  Sparkle, Robot, ArrowRight, X, FloppyDisk, Eye, FilePdf, Image as ImageIcon,
  PaperPlaneTilt, Spinner, ClipboardText,
} from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REWRITE_STYLES = [
  { key: 'politesse', label: 'Politesse' },
  { key: 'formel', label: 'Formel' },
  { key: 'professionnel', label: 'Professionnel' },
  { key: 'cordial', label: 'Cordial' },
  { key: 'direct', label: 'Direct' },
  { key: 'correction', label: 'Corriger l\'orthographe' },
];

const STATUS_LABELS = {
  received: { label: 'Recu', color: '#5E5F66', bg: '#F4F5F7' },
  in_progress: { label: 'En cours', color: '#92400E', bg: '#FEF3C7' },
  replied: { label: 'Repondu', color: '#065F46', bg: '#D1FAE5' },
  archived: { label: 'Archive', color: '#1F2024', bg: '#E5E7EB' },
};

// --- Upload modal ---
const UploadModal = ({ show, onClose, onUploaded, folders, projects }) => {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ folder_id: '', project_id: '', sender: '', subject: '', received_date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (show) {
      setFile(null);
      setForm({ folder_id: '', project_id: '', sender: '', subject: '', received_date: '', notes: '' });
    }
  }, [show]);
  if (!show) return null;
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Selectionnez un fichier');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ''));
      const res = await axios.post(`${API_URL}/api/correspondence`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Courrier ajoute');
      onUploaded(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload');
    } finally { setSaving(false); }
  };
  return (
    <div data-testid="corr-upload-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', overflowY: 'auto' }}>
      <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 640, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Envelope size={20} /> Nouveau courrier
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label className="form-label">Fichier (PDF, JPG, PNG)</label>
            <input type="file" accept="application/pdf,image/*" data-testid="corr-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} required className="form-input" />
            {file && <small style={{ color: '#5E5F66' }}>{file.name} - {(file.size / 1024).toFixed(0)} Ko</small>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label className="form-label">Emetteur (optionnel)</label>
              <input className="form-input" data-testid="corr-sender" value={form.sender}
                onChange={(e) => setForm(f => ({ ...f, sender: e.target.value }))} placeholder="Ex: URSSAF" />
            </div>
            <div>
              <label className="form-label">Date du courrier</label>
              <input type="date" className="form-input" value={form.received_date}
                onChange={(e) => setForm(f => ({ ...f, received_date: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="form-label">Objet (optionnel)</label>
            <input className="form-input" data-testid="corr-subject" value={form.subject}
              onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Ex: Mise en demeure paiement" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label className="form-label">Dossier (optionnel)</label>
              <Select value={form.folder_id || '__none__'} onValueChange={(v) => setForm(f => ({ ...f, folder_id: v === '__none__' ? '' : v }))}>
                <SelectTrigger style={{ fontSize: '0.85rem', height: 38 }}><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="__none__">Aucun</SelectItem>
                  {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="form-label">Chantier (optionnel)</label>
              <Select value={form.project_id || '__none__'} onValueChange={(v) => setForm(f => ({ ...f, project_id: v === '__none__' ? '' : v }))}>
                <SelectTrigger style={{ fontSize: '0.85rem', height: 38 }}><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="__none__">Aucun</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="form-label">Notes (optionnel)</label>
            <textarea className="form-input" rows={2} value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} style={{ resize: 'vertical', fontSize: '0.85rem' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="corr-upload-submit">
              <FloppyDisk size={16} /> {saving ? 'Envoi...' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- New folder modal ---
const FolderModal = ({ show, onClose, onSaved, projects }) => {
  const [name, setName] = useState('');
  const [project_id, setProjectId] = useState('');
  const [color, setColor] = useState('#1D4ED8');
  useEffect(() => { if (show) { setName(''); setProjectId(''); setColor('#1D4ED8'); } }, [show]);
  if (!show) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Nom requis');
    try {
      const res = await axios.post(`${API_URL}/api/correspondence/folders`,
        { name: name.trim(), project_id, color }, { withCredentials: true });
      toast.success('Dossier cree');
      onSaved(res.data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  return (
    <div data-testid="corr-folder-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 460 }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><FolderPlus size={20} /> Nouveau dossier</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
        </div>
        <form onSubmit={submit} style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label className="form-label">Nom du dossier</label>
            <input className="form-input" data-testid="corr-folder-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ex: URSSAF 2026, Litige Dupont..." required autoFocus />
          </div>
          <div>
            <label className="form-label">Lier a un chantier (optionnel)</label>
            <Select value={project_id || '__none__'} onValueChange={(v) => setProjectId(v === '__none__' ? '' : v)}>
              <SelectTrigger style={{ fontSize: '0.85rem', height: 38 }}><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value="__none__">Aucun</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label className="form-label" style={{ margin: 0 }}>Couleur</label>
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 50, height: 32, border: 'none', cursor: 'pointer' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" data-testid="corr-folder-submit">Creer</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Item detail panel ---
const ItemDetail = ({ item, onClose, onChange, onDelete, folders, projects }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [replying, setReplying] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [editForm, setEditForm] = useState({});
  useEffect(() => { setEditForm({ folder_id: item.folder_id || '', project_id: item.project_id || '', status: item.status || 'received' }); }, [item]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API_URL}/api/correspondence/${item.id}/analyze`, {}, { withCredentials: true });
      toast.success('Analyse Gemini terminee');
      onChange(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur analyse'); }
    finally { setAnalyzing(false); }
  };
  const suggestReply = async () => {
    setReplying(true);
    try {
      const res = await axios.post(`${API_URL}/api/correspondence/${item.id}/suggest-reply`, { instructions: '' }, { withCredentials: true });
      toast.success('Reponse generee');
      onChange(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur reponse'); }
    finally { setReplying(false); }
  };
  const updateField = async (field, value) => {
    try {
      const res = await axios.put(`${API_URL}/api/correspondence/${item.id}`, { [field]: value }, { withCredentials: true });
      onChange(res.data);
    } catch { /* ignore */ }
  };

  const fileUrl = `${API_URL}/api/correspondence/${item.id}/file`;
  const isPdf = (item.mime_type || '').includes('pdf');
  const status = STATUS_LABELS[item.status || 'received'];

  return (
    <div className="card" data-testid="corr-detail" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{item.subject || item.original_filename || 'Sans objet'}</h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#5E5F66' }}>
            De: <strong>{item.sender || 'inconnu'}</strong>
            {item.received_date ? ` - ${item.received_date}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ padding: '3px 8px', borderRadius: 4, background: status.bg, color: status.color, fontSize: '0.74rem', fontWeight: 700 }}>{status.label}</span>
          <button onClick={onClose} className="btn btn-secondary btn-sm"><X size={14} /></button>
        </div>
      </div>

      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, borderBottom: '1px solid #F0F1F3' }}>
        <div>
          <label className="form-label" style={{ fontSize: '0.74rem' }}>Dossier</label>
          <Select value={editForm.folder_id || '__none__'} onValueChange={(v) => { setEditForm(f => ({ ...f, folder_id: v === '__none__' ? '' : v })); updateField('folder_id', v === '__none__' ? '' : v); }}>
            <SelectTrigger style={{ fontSize: '0.82rem', height: 32 }}><SelectValue placeholder="Aucun" /></SelectTrigger>
            <SelectContent className="z-[9999]">
              <SelectItem value="__none__">Aucun</SelectItem>
              {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="form-label" style={{ fontSize: '0.74rem' }}>Chantier</label>
          <Select value={editForm.project_id || '__none__'} onValueChange={(v) => { setEditForm(f => ({ ...f, project_id: v === '__none__' ? '' : v })); updateField('project_id', v === '__none__' ? '' : v); }}>
            <SelectTrigger style={{ fontSize: '0.82rem', height: 32 }}><SelectValue placeholder="Aucun" /></SelectTrigger>
            <SelectContent className="z-[9999]">
              <SelectItem value="__none__">Aucun</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="form-label" style={{ fontSize: '0.74rem' }}>Statut</label>
          <Select value={editForm.status} onValueChange={(v) => { setEditForm(f => ({ ...f, status: v })); updateField('status', v); }}>
            <SelectTrigger style={{ fontSize: '0.82rem', height: 32 }}><SelectValue /></SelectTrigger>
            <SelectContent className="z-[9999]">
              {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div style={{ padding: 16, display: 'flex', gap: 8, flexWrap: 'wrap', borderBottom: '1px solid #F0F1F3' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => setShowPdf(s => !s)} data-testid="corr-toggle-view">
          {isPdf ? <FilePdf size={14} /> : <ImageIcon size={14} />} {showPdf ? 'Masquer' : 'Voir le scan'}
        </button>
        <button className="btn btn-primary btn-sm" onClick={analyze} disabled={analyzing} data-testid="corr-analyze-btn"
          style={{ background: '#7C3AED', borderColor: '#7C3AED' }}>
          {analyzing ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="bold" />} {item.ai_analyzed_at ? 'Re-analyser' : 'Analyser (Gemini)'}
        </button>
        <button className="btn btn-success btn-sm" onClick={suggestReply} disabled={replying || (!item.summary && !item.raw_text)}
          data-testid="corr-suggest-reply-btn"
          style={{ background: '#047857', color: 'white', borderColor: '#047857' }}>
          {replying ? <Spinner size={14} className="animate-spin" /> : <Robot size={14} weight="bold" />} Suggerer une reponse
        </button>
        <button className="btn btn-danger btn-sm" onClick={() => onDelete(item.id)} style={{ marginLeft: 'auto' }}>
          <Trash size={14} />
        </button>
      </div>

      {showPdf && (
        <div style={{ padding: 16, borderBottom: '1px solid #F0F1F3' }}>
          {isPdf ? (
            <object data={fileUrl} type="application/pdf" style={{ width: '100%', height: 480, border: '1px solid #E2E2E6', borderRadius: 6 }}>
              <p>PDF non affichable. <a href={fileUrl} target="_blank" rel="noreferrer">Ouvrir le fichier</a></p>
            </object>
          ) : (
            <img src={fileUrl} alt="scan" style={{ maxWidth: '100%', maxHeight: 480, borderRadius: 6, border: '1px solid #E2E2E6' }} />
          )}
        </div>
      )}

      {item.summary && (
        <div style={{ padding: 16, borderBottom: '1px solid #F0F1F3' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <ClipboardText size={14} weight="bold" />
            <strong style={{ fontSize: '0.88rem' }}>Resume Gemini</strong>
          </div>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.88rem', color: '#374151', lineHeight: 1.5 }}>{item.summary}</p>
        </div>
      )}

      {item.suggested_reply && (
        <div style={{ padding: 16, background: '#F0FDF4' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Robot size={14} weight="bold" color="#047857" />
            <strong style={{ fontSize: '0.88rem', color: '#065F46' }}>Reponse suggeree</strong>
            <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto', padding: '2px 6px' }}
              onClick={() => { navigator.clipboard.writeText(item.suggested_reply); toast.success('Copie dans le presse-papiers'); }}>
              Copier
            </button>
          </div>
          <textarea
            className="form-input"
            value={item.suggested_reply}
            onChange={(e) => onChange({ ...item, suggested_reply: e.target.value })}
            onBlur={() => updateField('suggested_reply', item.suggested_reply)}
            rows={10}
            style={{ width: '100%', fontSize: '0.88rem', whiteSpace: 'pre-wrap', resize: 'vertical', background: 'white' }}
            data-testid="corr-suggested-reply"
          />
        </div>
      )}
    </div>
  );
};

// --- AI Rewrite tool ---
const AiRewriteTool = () => {
  const [text, setText] = useState('');
  const [output, setOutput] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeStyle, setActiveStyle] = useState('');
  const [instructions, setInstructions] = useState('');
  const run = async (style) => {
    if (!text.trim()) return toast.error('Saisissez un texte d\'abord');
    setBusy(true); setActiveStyle(style); setOutput('');
    try {
      const res = await axios.post(`${API_URL}/api/correspondence/ai-rewrite`, { text, style, instructions }, { withCredentials: true });
      setOutput(res.data.text);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur Gemini'); }
    finally { setBusy(false); }
  };
  return (
    <div className="card" data-testid="corr-ai-rewrite" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Sparkle size={20} color="#7C3AED" weight="bold" />
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Assistant redactionnel (Gemini)</h3>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: '#5E5F66' }}>
        Saisissez votre texte ci-dessous puis choisissez un style de reformulation.
      </p>
      <textarea className="form-input" rows={6} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Votre brouillon a reformuler ou a corriger..."
        style={{ fontSize: '0.88rem', resize: 'vertical', width: '100%' }} data-testid="corr-rewrite-input" />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        {REWRITE_STYLES.map(s => (
          <button key={s.key} className="btn btn-secondary btn-sm"
            onClick={() => run(s.key)} disabled={busy}
            data-testid={`corr-rewrite-style-${s.key}`}
            style={busy && activeStyle === s.key ? { opacity: 0.6 } : {}}>
            {busy && activeStyle === s.key ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} />} {s.label}
          </button>
        ))}
      </div>
      <input className="form-input" placeholder="Consignes additionnelles (optionnel)" value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        style={{ marginTop: 10, fontSize: '0.85rem' }} />
      {output && (
        <div style={{ marginTop: 12, padding: 12, background: '#F8FAFC', borderRadius: 6, border: '1px solid #E2E8F0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong style={{ fontSize: '0.85rem' }}>Resultat ({REWRITE_STYLES.find(s => s.key === activeStyle)?.label})</strong>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard.writeText(output); toast.success('Copie'); }}>Copier</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setText(output)}>Reutiliser</button>
            </div>
          </div>
          <textarea readOnly value={output} rows={10}
            style={{ width: '100%', fontSize: '0.88rem', resize: 'vertical', background: 'white', border: '1px solid #E2E8F0', borderRadius: 4, padding: 8 }}
            data-testid="corr-rewrite-output" />
        </div>
      )}
    </div>
  );
};

// --- Main page ---
const CorrespondencePage = () => {
  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeFolder, setActiveFolder] = useState('all');
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showFolder, setShowFolder] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('mail');

  const fetchData = useCallback(async () => {
    try {
      const [f, i, p] = await Promise.all([
        axios.get(`${API_URL}/api/correspondence/folders`, { withCredentials: true }),
        axios.get(`${API_URL}/api/correspondence`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
      ]);
      setFolders(f.data || []);
      setItems(i.data || []);
      setProjects(p.data || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const s = (search || '').toLowerCase();
    return items.filter(it => {
      if (activeFolder === 'unfiled' && it.folder_id) return false;
      if (activeFolder !== 'all' && activeFolder !== 'unfiled' && it.folder_id !== activeFolder) return false;
      if (!s) return true;
      return ((it.subject || '') + (it.sender || '') + (it.summary || '') + (it.original_filename || '')).toLowerCase().includes(s);
    });
  }, [items, activeFolder, search]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce courrier ?')) return;
    try {
      await axios.delete(`${API_URL}/api/correspondence/${id}`, { withCredentials: true });
      toast.success('Supprime');
      setSelected(null);
      fetchData();
    } catch { toast.error('Erreur'); }
  };
  const handleDeleteFolder = async (id) => {
    if (!window.confirm('Supprimer ce dossier ? Les courriers ne seront pas supprimes (juste detaches).')) return;
    try {
      await axios.delete(`${API_URL}/api/correspondence/folders/${id}`, { withCredentials: true });
      toast.success('Dossier supprime');
      if (activeFolder === id) setActiveFolder('all');
      fetchData();
    } catch { toast.error('Erreur'); }
  };
  const handleItemChange = (updated) => {
    setItems(arr => arr.map(it => it.id === updated.id ? updated : it));
    setSelected(updated);
  };

  return (
    <div data-testid="correspondence-page">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Envelope size={28} weight="bold" /> Correspondance
        </h1>
        <p className="page-subtitle">Courriers postaux scannes - dossiers - assistant redactionnel IA</p>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid #E2E2E6' }}>
        <button className={`tab ${tab === 'mail' ? 'active' : ''}`} onClick={() => setTab('mail')} data-testid="corr-tab-mail">
          <Envelope size={14} weight="bold" /> Courriers
        </button>
        <button className={`tab ${tab === 'ai' ? 'active' : ''}`} onClick={() => setTab('ai')} data-testid="corr-tab-ai">
          <Sparkle size={14} weight="bold" /> Assistant IA
        </button>
      </div>

      {tab === 'ai' ? (
        <AiRewriteTool />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'flex-start' }}>
          {/* Left: folders */}
          <div className="card" style={{ padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: '0.88rem' }}>Dossiers</strong>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowFolder(true)} data-testid="corr-new-folder-btn" style={{ padding: '4px 8px' }}>
                <FolderPlus size={12} />
              </button>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <li>
                <button onClick={() => setActiveFolder('all')} data-testid="corr-folder-all"
                  style={{
                    width: '100%', textAlign: 'left', border: 'none', background: activeFolder === 'all' ? '#EFF6FF' : 'transparent',
                    color: activeFolder === 'all' ? '#1D4ED8' : '#374151', cursor: 'pointer', padding: '6px 8px',
                    borderRadius: 4, fontWeight: activeFolder === 'all' ? 700 : 500, fontSize: '0.85rem',
                  }}>
                  <Envelope size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Tous ({items.length})
                </button>
              </li>
              <li>
                <button onClick={() => setActiveFolder('unfiled')}
                  style={{
                    width: '100%', textAlign: 'left', border: 'none', background: activeFolder === 'unfiled' ? '#EFF6FF' : 'transparent',
                    color: activeFolder === 'unfiled' ? '#1D4ED8' : '#374151', cursor: 'pointer', padding: '6px 8px',
                    borderRadius: 4, fontSize: '0.85rem',
                  }}>
                  Non classes ({items.filter(i => !i.folder_id).length})
                </button>
              </li>
              {folders.map(f => (
                <li key={f.id} style={{ display: 'flex', alignItems: 'center' }}>
                  <button onClick={() => setActiveFolder(f.id)}
                    data-testid={`corr-folder-${f.id}`}
                    style={{
                      flex: 1, textAlign: 'left', border: 'none', background: activeFolder === f.id ? '#EFF6FF' : 'transparent',
                      color: activeFolder === f.id ? '#1D4ED8' : '#374151', cursor: 'pointer', padding: '6px 8px',
                      borderRadius: 4, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                    <Folder size={12} color={f.color || '#1D4ED8'} weight="fill" />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '0.74rem', color: '#9CA3AF' }}>{f.items_count}</span>
                  </button>
                  <button onClick={() => handleDeleteFolder(f.id)} className="btn btn-secondary btn-sm" style={{ padding: 2, marginLeft: 2 }}>
                    <Trash size={10} />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: list + detail */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: '1 1 280px' }}>
                <MagnifyingGlass size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', pointerEvents: 'none' }} />
                <input className="form-input" placeholder="Rechercher (emetteur, objet, contenu)..." value={search}
                  onChange={(e) => setSearch(e.target.value)} style={{ paddingLeft: 36, fontSize: '0.85rem' }}
                  data-testid="corr-search-input" />
              </div>
              <button className="btn btn-primary" onClick={() => setShowUpload(true)} data-testid="corr-new-mail-btn">
                <Plus size={16} weight="bold" /> Ajouter un courrier
              </button>
            </div>

            {filtered.length === 0 ? (
              <div className="empty-state">
                <Envelope size={48} className="empty-state-icon" />
                <p className="empty-state-text">Aucun courrier dans cette vue</p>
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="data-table" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Emetteur</th>
                      <th>Objet</th>
                      <th>Dossier / Chantier</th>
                      <th>Statut</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(it => {
                      const stat = STATUS_LABELS[it.status || 'received'];
                      return (
                        <tr key={it.id} data-testid={`corr-row-${it.id}`}
                          onClick={() => setSelected(it)}
                          style={{ cursor: 'pointer', background: selected?.id === it.id ? '#F0F9FF' : undefined }}>
                          <td style={{ fontSize: '0.82rem' }}>{it.received_date || '-'}</td>
                          <td style={{ fontWeight: 600 }}>{it.sender || <span style={{ color: '#9CA3AF' }}>?</span>}</td>
                          <td>{it.subject || it.original_filename}</td>
                          <td style={{ fontSize: '0.78rem' }}>
                            {it.folder_name && <span style={{ color: '#1D4ED8' }}>{it.folder_name}</span>}
                            {it.folder_name && it.project_name ? ' / ' : ''}
                            {it.project_name && <span style={{ color: '#047857' }}>{it.project_name}</span>}
                          </td>
                          <td>
                            <span style={{ padding: '2px 8px', borderRadius: 4, background: stat.bg, color: stat.color, fontSize: '0.72rem', fontWeight: 700 }}>{stat.label}</span>
                          </td>
                          <td>
                            <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setSelected(it); }} style={{ padding: 4 }}>
                              <Eye size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {selected && <ItemDetail item={selected} onClose={() => setSelected(null)}
              onChange={handleItemChange} onDelete={handleDelete}
              folders={folders} projects={projects} />}
          </div>
        </div>
      )}

      <UploadModal show={showUpload} onClose={() => setShowUpload(false)}
        onUploaded={(newItem) => { setItems(arr => [newItem, ...arr]); setSelected(newItem); }}
        folders={folders} projects={projects} />
      <FolderModal show={showFolder} onClose={() => setShowFolder(false)}
        onSaved={(f) => setFolders(arr => [...arr, f].sort((a, b) => a.name.localeCompare(b.name)))}
        projects={projects} />
    </div>
  );
};

export default CorrespondencePage;
